import type {MutableRefObject} from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from 'setup'
import {requestOnboardingAiAllocation} from 'setup/redux/Actions/Onboarding/teacherAllocationAi'

import type {WizardData} from '../index'
import type {
  AiAllocationAction,
  AiAllocationRequest,
  AiAllocationResponse,
  ConversationTurn,
  LessonPlanningData,
} from '../types/teacherAllocationAi'
import {findTeacherByRef, getTeacherFullName, parseAllocationSlotKey} from '../utils/allocationIds'
import {buildAllocationContext} from '../utils/buildAllocationContext'
import {buildDisambiguationFollowUp} from '../utils/buildDisambiguationFollowUp'
import {
  getRemovedSlotKeys,
  mapActionsToLessonPlanningData,
} from '../utils/mapActionsToLessonPlanningData'
import {
  buildMockAllocationResponse,
  getEffectiveAllocationActions,
  hasAllocationConfirmation,
  normalizeAllocationAiResponse,
} from '../utils/normalizeAllocationAiResponse'
import {
  ALL_FILTER_VALUE,
  type AllocationCounts,
  type ProposalChange,
  isSettingsOnlyProposal,
  projectCounts,
  proposalIsStale,
  snapshotProposalSlots,
} from '../utils/teacherAllocationModel'
import {
  MAX_UNDO_ENTRIES,
  type VersiItem,
  type VersiPendingProposal,
  type VersiUndoEntry,
  buildVersiSessionKey,
  clearVersiSession,
  readVersiSession,
  writeVersiSession,
} from '../utils/versiAllocationSession'

export type UseVersiAllocationParams = {
  manualDataRef: MutableRefObject<WizardData['manualData']>
  lessonPlanningData: LessonPlanningData
  setLessonPlanningData: React.Dispatch<React.SetStateAction<LessonPlanningData>>
  deletedSlots: ReadonlySet<string>
  setDeletedSlots: React.Dispatch<React.SetStateAction<Set<string>>>
  /** Saved progress across every branch and level (Versi never shows scoped progress). */
  counts: AllocationCounts
  /** "Working on": Branch / Level; `''` is All. */
  workingScope: {branch: string; level: string}
  normalizeBranchName: (branch?: string) => string
  getSubjectsForClass: (classItem: any) => string[]
  getLessonClassKey: (className: string, branch?: string, curriculum?: string) => string
  defaultFrequency: string
  defaultDuration: string
  /** Called with the slots an Apply changed. */
  onApplied?: (slotKeys: string[]) => void
}

const useMock =
  String(import.meta.env.VITE_MOCK_ONBOARDING_AI_ALLOCATION ?? '').toLowerCase() === 'true'

let itemSeq = 0
const nextId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(itemSeq += 1)}`

/**
 * Versi, the Teacher Allocation assistant (handover, Point 4). Every change it suggests — direct
 * or grouped assignments, weekly frequency, consecutive periods, marking a subject as not taught —
 * arrives as a proposal in the conversation and is saved only when the user applies it. One
 * approval per displayed proposal: previewing it in the grid needs no second one.
 *
 * Applying first checks the affected slots against the snapshot taken with the proposal; when
 * they changed meanwhile, the proposal is refreshed and must be approved again. Undo after an
 * apply puts back exactly the slots that apply changed.
 */
export function useVersiAllocation(params: UseVersiAllocationParams) {
  const {
    manualDataRef,
    lessonPlanningData,
    setLessonPlanningData,
    deletedSlots,
    setDeletedSlots,
    counts,
    workingScope,
    normalizeBranchName,
    getSubjectsForClass,
    getLessonClassKey,
    defaultFrequency,
    defaultDuration,
    onApplied,
  } = params

  const dispatch = useDispatch()
  const intl = useIntl()
  const onAppliedRef = useRef(onApplied)
  onAppliedRef.current = onApplied

  const urddId = useSelector<RootState>((state) => state.userReducer.urddId) as string | null
  const tenant = useSelector<RootState>((state) => state.userReducer.tenant) as string
  const storageKey = useMemo(() => buildVersiSessionKey(urddId, tenant), [tenant, urddId])

  // Read during the first render so returning to the step paints the conversation at once.
  const [restored] = useState(() => readVersiSession(storageKey))
  const [items, setItems] = useState<VersiItem[]>(restored?.items ?? [])
  const [conversationHistory, setConversationHistory] = useState<ConversationTurn[]>(
    restored?.conversationHistory ?? []
  )
  const [pending, setPending] = useState<VersiPendingProposal | null>(restored?.pending ?? null)
  const [undo, setUndo] = useState<Record<string, VersiUndoEntry>>(restored?.undo ?? {})
  const [examplesCollapsed, setExamplesCollapsed] = useState(restored?.examplesCollapsed ?? false)
  const [isLoading, setIsLoading] = useState(false)
  const [isApplying, setIsApplying] = useState(false)
  const lastPromptRef = useRef('')
  // Read by an in-flight request when its answer lands: the proposal waiting at that moment.
  const pendingRef = useRef(pending)
  pendingRef.current = pending

  useEffect(() => {
    if (items.length === 0 && conversationHistory.length === 0) return
    writeVersiSession(storageKey, {
      conversationHistory,
      items,
      pending,
      undo,
      examplesCollapsed,
    })
  }, [conversationHistory, examplesCollapsed, items, pending, storageKey, undo])

  const isSlotDeleted = useCallback(
    (classKey: string, subject: string) => deletedSlots.has(`${classKey}|||${subject}`),
    [deletedSlots]
  )

  const buildContext = useCallback(() => {
    const manualData = manualDataRef.current
    const branch = workingScope.branch
    const level = workingScope.level
    const classes = ((manualData.classes as any[]) ?? []).filter(
      (c: any) =>
        (branch === ALL_FILTER_VALUE || normalizeBranchName(c?.branch) === branch) &&
        (level === ALL_FILTER_VALUE || String(c?.level ?? '').trim() === level)
    )
    return buildAllocationContext({
      manualData: {...manualData, classes},
      lessonPlanningData,
      scope: 'all_branches',
      activeFilters: {},
      normalizeBranchName,
      getSubjectsForClass,
      getLessonClassKey,
      isSlotDeleted,
    })
  }, [
    getLessonClassKey,
    getSubjectsForClass,
    isSlotDeleted,
    lessonPlanningData,
    manualDataRef,
    normalizeBranchName,
    workingScope.branch,
    workingScope.level,
  ])

  const callApi = useCallback(
    (body: AiAllocationRequest) =>
      new Promise<AiAllocationResponse>((resolve, reject) => {
        if (useMock) {
          resolve(
            buildMockAllocationResponse(body.context, body.prompt, {
              confirmOverwrite: body.confirmOverwrite,
              pendingActions: body.pendingActions,
            })
          )
          return
        }
        dispatch(
          requestOnboardingAiAllocation(
            body,
            (res: any) => resolve(normalizeAllocationAiResponse(res)),
            (err: any) => reject(new Error(String(err?.message ?? '')))
          )
        )
      }),
    [dispatch]
  )

  /** The proposal rows: current and proposed values; no-ops (same teacher, same value) dropped. */
  const describeActions = useCallback(
    (actions: AiAllocationAction[]): ProposalChange[] => {
      const teachers = (manualDataRef.current.teachers as any[]) ?? []
      const bySlot = new Map<string, ProposalChange>()
      actions.forEach((action) => {
        const {classKey, subjectName: subject} = parseAllocationSlotKey(action.slotKey)
        if (!classKey || !subject) return
        const cell = lessonPlanningData[classKey]?.[subject]
        const currentTeacher = String(cell?.teacher ?? '').trim()
        const base = {slotKey: action.slotKey, classKey, subject}
        const key = (kind: string) => `${action.slotKey}|${kind}`
        if (action.type === 'assign' || action.type === 'replace') {
          const match = findTeacherByRef(teachers, action.teacherEmployeeId, action.teacherName)
          const proposed = match
            ? getTeacherFullName(match)
            : String(action.teacherName ?? '').trim()
          if (!proposed || proposed === currentTeacher) return
          bySlot.set(key('teacher'), {
            ...base,
            kind: currentTeacher ? 'replace' : 'new',
            current: currentTeacher,
            proposed,
          })
        } else if (action.type === 'unassign') {
          if (!currentTeacher) return
          bySlot.set(key('teacher'), {
            ...base,
            kind: 'unassign',
            current: currentTeacher,
            proposed: '',
          })
        } else if (action.type === 'set_frequency') {
          const current = String(cell?.frequency || defaultFrequency)
          if (!action.frequency || action.frequency === current) return
          bySlot.set(key('frequency'), {
            ...base,
            kind: 'frequency',
            current,
            proposed: action.frequency,
          })
        } else if (action.type === 'set_duration') {
          const current = String(cell?.duration || defaultDuration)
          if (!action.duration || action.duration === current) return
          bySlot.set(key('duration'), {
            ...base,
            kind: 'duration',
            current,
            proposed: action.duration,
          })
        } else if (action.type === 'remove') {
          if (deletedSlots.has(action.slotKey)) return
          bySlot.set(key('teacher'), {
            ...base,
            kind: 'remove',
            current: currentTeacher,
            proposed: '',
          })
        }
      })
      return Array.from(bySlot.values())
    },
    [defaultDuration, defaultFrequency, deletedSlots, lessonPlanningData, manualDataRef]
  )

  const pushItems = useCallback(
    (...next: VersiItem[]) => setItems((prev) => [...prev, ...next]),
    []
  )

  const markProposal = useCallback(
    (itemId: string, status: 'applied' | 'discarded' | 'superseded') =>
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId && item.kind === 'proposal' ? {...item, status} : item
        )
      ),
    []
  )

  /**
   * Sends an instruction. `displayText` is what the user bubble shows (a picked clarification is
   * shown by its label while the resolved prompt is sent); `silent` sends without a user bubble
   * (refreshing a stale proposal).
   */
  const runRequest = useCallback(
    async (prompt: string, options?: {displayText?: string; silent?: boolean}) => {
      const trimmed = prompt.trim()
      if (!trimmed) return
      lastPromptRef.current = trimmed
      setExamplesCollapsed(true)
      if (!options?.silent) {
        pushItems({id: nextId('u'), kind: 'user', text: options?.displayText ?? trimmed})
      }
      const historyBase = conversationHistory
      const historyWithUser = [...historyBase, {role: 'user' as const, content: trimmed}]
      setConversationHistory(historyWithUser)
      setIsLoading(true)
      try {
        const context = buildContext()
        if (context.subjects.length === 0) {
          pushItems({id: nextId('n'), kind: 'notice', tone: 'warning', textKey: 'NO_SUBJECTS'})
          return
        }
        if (context.teachers.length === 0) {
          pushItems({id: nextId('n'), kind: 'notice', tone: 'warning', textKey: 'NO_TEACHERS'})
          return
        }
        const response = await callApi({
          prompt: trimmed,
          context,
          conversationHistory: historyBase,
          workingScope: {
            branch: workingScope.branch || null,
            level: workingScope.level || null,
          },
        })
        setConversationHistory([
          ...historyWithUser,
          {role: 'assistant', content: response.previewMessage},
        ])

        if (response.disambiguation) {
          const {matchedName, options: choices} = response.disambiguation
          pushItems({
            id: nextId('c'),
            kind: 'clarify',
            question: response.previewMessage,
            options: choices.map((o) => ({
              label: o.displayName || o.name,
              employeeId: o.employeeId,
              matchedName,
            })),
            answered: false,
          })
          return
        }

        const actions = getEffectiveAllocationActions(response)
        const changes = describeActions(actions)
        if (changes.length === 0) {
          pushItems({
            id: nextId('a'),
            kind: 'assistant',
            text:
              response.previewMessage ||
              intl.formatMessage({id: 'ONBOARDING_WIZARD.TEACHER_ALLOCATION.AI.NO_CHANGES'}),
          })
          return
        }

        // A new proposal replaces the one still waiting: one approval per displayed proposal.
        if (pendingRef.current) markProposal(pendingRef.current.itemId, 'superseded')
        const itemId = nextId('p')
        pushItems({
          id: itemId,
          kind: 'proposal',
          message: response.previewMessage,
          changes,
          warnings: response.warnings,
          status: 'pending',
        })
        setPending({
          itemId,
          prompt: trimmed,
          actions,
          needsConfirmCall: hasAllocationConfirmation(response),
          snapshot: snapshotProposalSlots(
            Array.from(new Set(changes.map((c) => c.slotKey))),
            lessonPlanningData,
            deletedSlots
          ),
        })
      } catch {
        pushItems({id: nextId('n'), kind: 'notice', tone: 'error', textKey: 'ERROR'})
      } finally {
        setIsLoading(false)
      }
    },
    [
      buildContext,
      callApi,
      conversationHistory,
      deletedSlots,
      describeActions,
      intl,
      lessonPlanningData,
      markProposal,
      pushItems,
      workingScope.branch,
      workingScope.level,
    ]
  )

  const send = useCallback(
    (prompt: string) => {
      if (isLoading || isApplying) return
      runRequest(prompt)
    },
    [isApplying, isLoading, runRequest]
  )

  const answerClarification = useCallback(
    (itemId: string, option: {label: string; employeeId: string; matchedName: string}) => {
      if (isLoading || isApplying) return
      setItems((prev) =>
        prev.map((item) =>
          item.id === itemId && item.kind === 'clarify' ? {...item, answered: true} : item
        )
      )
      runRequest(
        buildDisambiguationFollowUp(lastPromptRef.current, option.matchedName, option.employeeId),
        {displayText: option.label}
      )
    },
    [isApplying, isLoading, runRequest]
  )

  const pendingItem = useMemo(() => {
    if (!pending) return null
    const item = items.find((i) => i.id === pending.itemId)
    return item && item.kind === 'proposal' && item.status === 'pending' ? item : null
  }, [items, pending])

  /**
   * "New chat": drops the conversation, any proposal still waiting and the undo history.
   * Changes already applied to the grid stay as they are.
   */
  const startNewChat = useCallback(() => {
    if (isLoading || isApplying) return
    setItems([])
    setConversationHistory([])
    setPending(null)
    setUndo({})
    setExamplesCollapsed(false)
    lastPromptRef.current = ''
    clearVersiSession(storageKey)
  }, [isApplying, isLoading, storageKey])

  const discard = useCallback(() => {
    if (!pending || isApplying) return
    markProposal(pending.itemId, 'discarded')
    setPending(null)
    pushItems({id: nextId('n'), kind: 'notice', tone: 'info', textKey: 'DISCARDED'})
  }, [isApplying, markProposal, pending, pushItems])

  /** Resolves to what happened: applied, refreshed because stale, failed, or nothing to do. */
  const apply = useCallback(async (): Promise<'applied' | 'stale' | 'error' | 'noop'> => {
    if (!pending || !pendingItem || isApplying || isLoading) return 'noop'

    // Checked against the proposal's original data: a change made meanwhile (by hand, an upload,
    // another apply) refreshes the proposal, which then needs approving again.
    if (proposalIsStale(pending.snapshot, lessonPlanningData, deletedSlots)) {
      markProposal(pending.itemId, 'superseded')
      setPending(null)
      pushItems({id: nextId('n'), kind: 'notice', tone: 'warning', textKey: 'STALE'})
      runRequest(pending.prompt, {silent: true})
      return 'stale'
    }

    setIsApplying(true)
    try {
      let actions = pending.actions
      if (pending.needsConfirmCall) {
        const response = await callApi({
          prompt: 'yes',
          context: buildContext(),
          conversationHistory,
          confirmOverwrite: true,
          pendingActions: pending.actions,
        })
        actions = getEffectiveAllocationActions(response)
        if (actions.length === 0) throw new Error('empty')
      }

      const slotKeys = Array.from(new Set(actions.map((a) => a.slotKey).filter(Boolean)))
      const before: VersiUndoEntry = {}
      slotKeys.forEach((key) => {
        const {classKey, subjectName} = parseAllocationSlotKey(key)
        const cell = lessonPlanningData[classKey]?.[subjectName]
        before[key] = {cell: cell ? {...cell} : null, deleted: deletedSlots.has(key)}
      })

      const nextGrid = mapActionsToLessonPlanningData({
        actions,
        context: buildContext(),
        lessonPlanningData,
        manualData: manualDataRef.current,
        defaultFrequency,
        defaultDuration,
      })
      const removed = getRemovedSlotKeys(actions)
      setLessonPlanningData(nextGrid)
      if (removed.length > 0) {
        setDeletedSlots((prev) => {
          const next = new Set(prev)
          removed.forEach((key) => next.add(key))
          return next
        })
      }

      const undoId = nextId('undo')
      setUndo((prev) => {
        const next = {...prev, [undoId]: before}
        const ids = Object.keys(next)
        return ids.length > MAX_UNDO_ENTRIES
          ? Object.fromEntries(ids.slice(-MAX_UNDO_ENTRIES).map((id) => [id, next[id]]))
          : next
      })
      const after = projectCounts(counts, pendingItem.changes)
      markProposal(pending.itemId, 'applied')
      setPending(null)
      pushItems({
        id: nextId('r'),
        kind: 'result',
        applied: pendingItem.changes.length,
        filled: after.filled,
        total: after.total,
        undoId,
        undone: false,
        settingsOnly: isSettingsOnlyProposal(pendingItem.changes),
      })
      onAppliedRef.current?.(slotKeys)
      return 'applied'
    } catch {
      pushItems({id: nextId('n'), kind: 'notice', tone: 'error', textKey: 'APPLY_ERROR'})
      return 'error'
    } finally {
      setIsApplying(false)
    }
  }, [
    buildContext,
    callApi,
    conversationHistory,
    counts,
    defaultDuration,
    defaultFrequency,
    deletedSlots,
    isApplying,
    isLoading,
    lessonPlanningData,
    manualDataRef,
    markProposal,
    pending,
    pendingItem,
    pushItems,
    runRequest,
    setDeletedSlots,
    setLessonPlanningData,
  ])

  const undoResult = useCallback(
    (resultId: string) => {
      const item = items.find((i) => i.id === resultId)
      if (!item || item.kind !== 'result' || item.undone) return
      const entry = undo[item.undoId]
      if (!entry) return
      setLessonPlanningData((prev) => {
        const next = {...prev}
        Object.entries(entry).forEach(([key, {cell}]) => {
          const {classKey, subjectName} = parseAllocationSlotKey(key)
          const map = {...(next[classKey] ?? {})}
          if (cell) map[subjectName] = {...cell}
          else delete map[subjectName]
          next[classKey] = map
        })
        return next
      })
      setDeletedSlots((prev) => {
        const next = new Set(prev)
        Object.entries(entry).forEach(([key, {deleted}]) => {
          if (deleted) next.add(key)
          else next.delete(key)
        })
        return next
      })
      setUndo((prev) => {
        const next = {...prev}
        delete next[item.undoId]
        return next
      })
      setItems((prev) =>
        prev.map((i) => (i.id === resultId && i.kind === 'result' ? {...i, undone: true} : i))
      )
      pushItems({id: nextId('n'), kind: 'notice', tone: 'info', textKey: 'UNDONE'})
    },
    [items, pushItems, setDeletedSlots, setLessonPlanningData, undo]
  )

  /** Pending changes by slot, for the grid's "Preview in full grid" mode. */
  const previewChanges = useMemo(() => {
    const map = new Map<string, ProposalChange[]>()
    pendingItem?.changes.forEach((change) => {
      map.set(change.slotKey, [...(map.get(change.slotKey) ?? []), change])
    })
    return map
  }, [pendingItem])

  return {
    items,
    pendingItem,
    previewChanges,
    isLoading,
    isApplying,
    examplesCollapsed,
    setExamplesCollapsed,
    hasConversation: items.length > 0,
    send,
    apply,
    discard,
    undoResult,
    answerClarification,
    canUndo: (undoId: string) => Boolean(undo[undoId]),
    startNewChat,
    canStartNewChat: items.length > 0 && !isLoading && !isApplying,
  }
}

export type VersiAllocation = ReturnType<typeof useVersiAllocation>
